import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaFilePdf, FaUpload, FaExternalLinkAlt, FaTrash } from 'react-icons/fa';

import { apiService } from '../../../components/Config/api.service';
import { notify } from '../../../components/Utils/Notifications';
import { ouvrirPdf } from '../../../components/Utils/ouvrirPdf';

// Au-delà, Odoo refuse le fichier : on le dit avant l'envoi.
const TAILLE_MAX = 10 * 1024 * 1024;

/**
 * Le contrat signé, scanné et déposé par la RH (Yros/Kavero#489).
 *
 * Aucun modèle ne génère le contrat de travail : le document qui fait foi est
 * celui que les deux parties ont signé. Le salarié le retrouve dans
 * « Mon contrat », où il peut seulement l'ouvrir ou le télécharger.
 */
function ContratSigne({ contratId, nom, onChange }) {
  const { t } = useTranslation();
  const champ = useRef(null);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [confirmer, setConfirmer] = useState(false);

  const executer = async (action) => {
    setEnCours(true);
    setErreur(null);
    try {
      await action();
    } catch (e) {
      setErreur(e?.response?.data?.error || t('contratSigne.erreur'));
    } finally {
      setEnCours(false);
    }
  };

  const deposer = (fichier) => {
    if (!fichier) return;
    if (fichier.type !== 'application/pdf') { setErreur(t('contratSigne.pasUnPdf')); return; }
    if (fichier.size > TAILLE_MAX) { setErreur(t('contratSigne.tropLourd')); return; }
    executer(async () => {
      const reponse = await apiService.deposerContratSigne(contratId, fichier);
      onChange(reponse.signedDocument);
      notify(t('contratSigne.depose'), '', 2);
    });
  };

  const retirer = () => executer(async () => {
    await apiService.retirerContratSigne(contratId);
    setConfirmer(false);
    onChange(null);
  });

  const bouton = 'inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg disabled:opacity-50';
  return (
    <section data-testid="contrat-signe" className="mt-6 rounded-xl border border-slate-200 p-4 space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-slate-800">{t('contratSigne.titre')}</h3>
        <p className="text-xs text-slate-500">{t('contratSigne.aide')}</p>
      </div>
      <input ref={champ} type="file" accept="application/pdf" className="hidden" data-testid="contrat-signe-fichier"
        onChange={(e) => { deposer(e.target.files?.[0]); e.target.value = ''; }} />
      <div className="flex flex-wrap items-center gap-2">
        {nom ? (
          <span data-testid="contrat-signe-nom" className="inline-flex items-center gap-2 text-sm text-slate-700 min-w-0 mr-auto">
            <FaFilePdf className="text-red-600 shrink-0" aria-hidden="true" /><span className="truncate">{nom}</span>
          </span>
        ) : (
          <span className="text-sm text-slate-500 mr-auto">{t('contratSigne.aucun')}</span>
        )}
        {nom && (
          <button type="button" data-testid="contrat-signe-ouvrir" disabled={enCours}
            onClick={() => executer(() => ouvrirPdf(() => apiService.lireContratSigne(contratId), nom))}
            className={`${bouton} border border-slate-300 text-slate-700 hover:bg-slate-50`}>
            <FaExternalLinkAlt aria-hidden="true" />{t('contratSigne.ouvrir')}
          </button>
        )}
        <button type="button" data-testid="contrat-signe-deposer" disabled={enCours} onClick={() => champ.current?.click()}
          className={`${bouton} bg-[--color-primary] text-white hover:bg-[--bg-hover]`}>
          <FaUpload aria-hidden="true" />{nom ? t('contratSigne.remplacer') : t('contratSigne.deposer')}
        </button>
        {nom && !confirmer && (
          <button type="button" data-testid="contrat-signe-retirer" disabled={enCours} onClick={() => setConfirmer(true)}
            className={`${bouton} text-red-700 hover:bg-red-50`}>
            <FaTrash aria-hidden="true" />{t('contratSigne.retirer')}
          </button>
        )}
      </div>
      {confirmer && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-800">
          <span className="mr-auto">{t('contratSigne.confirmerRetrait')}</span>
          <button type="button" onClick={() => setConfirmer(false)} className="px-3 py-1.5 text-slate-700 hover:underline">
            {t('contratSigne.annuler')}
          </button>
          <button type="button" data-testid="contrat-signe-confirmer-retrait" disabled={enCours} onClick={retirer}
            className="px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
            {t('contratSigne.retirer')}
          </button>
        </div>
      )}
      {erreur && <p role="alert" data-testid="contrat-signe-erreur" className="text-sm text-red-700">{erreur}</p>}
    </section>
  );
}

export default ContratSigne;
